import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/useUserStore';
import type { QueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useFlagsmithUser } from '../hooks/useFlagsmithUser';

export function AuthProvider({
  children,
  queryClient,
  fallback = (
    <div className="h-screen w-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin" />
    </div>
  ),
}: {
  children: React.ReactNode;
  queryClient: QueryClient;
  fallback?: React.ReactNode;
}) {
  const { initialized, setSession, setUser, checkSession, user } =
    useUserStore();
  const { clearIdentity, identifyWithTraits } = useFlagsmithUser();

  // Identify user with Flagsmith when user changes
  useEffect(() => {
    if (user) {
      // Create a traits object with all user traits
      const traits: Record<string, string | boolean> = {};

      if (user.email) {
        traits.email = user.email;
      }

      // Add any other relevant user traits here
      // For example:
      // traits.plan = user.user_metadata?.plan || 'free';
      // traits.isAdmin = user.app_metadata?.isAdmin || false;

      // Use identifyWithTraits to ensure traits are set after identification
      identifyWithTraits(user.id, traits);
    } else {
      // Clear identity when user is null (logged out)
      clearIdentity();
    }
  }, [user, identifyWithTraits, clearIdentity]);

  useEffect(() => {
    // Check initial session
    checkSession();

    // Set up auth state change subscription
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setSession(session);
        setUser(session?.user ?? null);

        // Reset React Query cache when a user signs in
        // This ensures that the new user doesn't see the previous user's data
        queryClient.invalidateQueries();
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);

        // Reset React Query cache when a user signs out
        // This ensures that the next user who signs in doesn't see the previous user's data
        queryClient.clear();
      }
    });

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient, checkSession, setSession, setUser]);

  if (!initialized) {
    return fallback;
  }

  return <>{children}</>;
}
